import {
  Add as AddIcon,
  Assignment as AssignmentIcon,
  CalendarToday as CalendarIcon,
  EventNote as EventNoteIcon,
  FilterList as FilterListIcon,
  LocationOn as LocationIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TablePagination,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import autorizacoesService from "../services/autorizacoes";
import AutorizacaoStatusChip from "../components/AutorizacaoStatusChip";
import {
  statusAutorizacaoLabels,
  tipoAutorizacaoEnum,
} from "../utils/constants";
import { formatDateToShow } from "../utils/formatters";

const tipoLabel = (tipo) =>
  tipoAutorizacaoEnum.find((t) => t.value === tipo)?.label ?? "—";

function MinhasAutorizacoes() {
  const theme = useTheme();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [filtroStatus, setFiltroStatus] = useState("");
  const [filtroAplicado, setFiltroAplicado] = useState("");
  const navigate = useNavigate();

  const carregar = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const result = await autorizacoesService.listarMinhasSolicitacoes({
        pageNumber: page + 1,
        pageSize,
      });
      let data = result.items;
      if (filtroAplicado !== "") {
        data = data.filter((i) => String(i.status) === String(filtroAplicado));
      }
      setItems(data);
      setTotal(filtroAplicado !== "" ? data.length : result.totalCount);
    } catch (err) {
      setError(err?.message || "Erro ao carregar autorizações.");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filtroAplicado]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const handleFiltrar = () => {
    setPage(0);
    setFiltroAplicado(filtroStatus);
  };

  const handleLimparFiltro = () => {
    setFiltroStatus("");
    setPage(0);
    setFiltroAplicado("");
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
      <Box sx={{ mt: 4, mb: 8 }}>
        {/* Cabeçalho */}
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "stretch", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            mb: 4,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
            <AssignmentIcon
              sx={{ fontSize: { xs: 32, md: 40 }, color: theme.palette.primary.main, mr: 2 }}
            />
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.1rem", md: "1.5rem" },
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Minhas Autorizações
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            fullWidth
            onClick={() => navigate("/nova-autorizacao")}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Nova Autorização
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Filtros */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            borderRadius: 2,
          }}
        >
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filtroStatus}
                  label="Status"
                  onChange={(e) => setFiltroStatus(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {Object.entries(statusAutorizacaoLabels).map(([v, l]) => (
                    <MenuItem key={v} value={v}>
                      {l}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button
                  variant="contained"
                  startIcon={<FilterListIcon />}
                  onClick={handleFiltrar}
                  fullWidth
                  sx={{ textTransform: "none", width: { xs: "100%", sm: "auto" } }}
                >
                  Filtrar
                </Button>
                {filtroAplicado !== "" && (
                  <Button
                    variant="outlined"
                    onClick={handleLimparFiltro}
                    fullWidth
                    sx={{ textTransform: "none", width: { xs: "100%", sm: "auto" } }}
                  >
                    Limpar
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* Lista */}
        <Grid container spacing={2}>
          {items.length === 0 ? (
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 6,
                  textAlign: "center",
                  backgroundColor: alpha(theme.palette.primary.main, 0.02),
                  border: `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderRadius: 2,
                }}
              >
                <EventNoteIcon
                  sx={{
                    fontSize: 56,
                    color: alpha(theme.palette.primary.main, 0.3),
                    mb: 2,
                  }}
                />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Nenhuma autorização encontrada
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Solicite uma nova autorização para começar.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => navigate("/nova-autorizacao")}
                  sx={{ borderRadius: 2, textTransform: "none" }}
                >
                  Nova Autorização
                </Button>
              </Paper>
            </Grid>
          ) : (
            items.map((it) => (
              <Grid item xs={12} key={it.id}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    transition: "all 0.25s ease",
                    "&:hover": {
                      boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
                      transform: "translateY(-2px)",
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                    },
                  }}
                >
                  <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
                    <Grid container spacing={2} alignItems="center">
                      {/* Tipo + Nome */}
                      <Grid item xs={12} sm={3}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          {tipoLabel(it.tipoAutorizacao)}
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, lineHeight: 1.2 }}
                        >
                          {it.nomeEvento}
                        </Typography>
                      </Grid>

                      {/* Data do evento */}
                      <Grid item xs={6} sm={2}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                        >
                          <CalendarIcon
                            sx={{ fontSize: 14, color: "text.secondary" }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            Evento
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatDateToShow(it.dataEvento)}
                        </Typography>
                      </Grid>

                      {/* Local */}
                      <Grid item xs={6} sm={2}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                        >
                          <LocationIcon
                            sx={{ fontSize: 14, color: "text.secondary" }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            Local
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: { xs: "normal", sm: "nowrap" },
                            maxWidth: { sm: 160 },
                          }}
                        >
                          {it.localEvento}
                        </Typography>
                      </Grid>

                      {/* Data da solicitação */}
                      <Grid item xs={6} sm={2}>
                        <Typography variant="caption" color="text.secondary">
                          Solicitado em
                        </Typography>
                        <Typography variant="body2">
                          {formatDateToShow(it.createAt)}
                        </Typography>
                      </Grid>

                      {/* Status */}
                      <Grid item xs={6} sm={2}>
                        <AutorizacaoStatusChip status={it.status} />
                      </Grid>

                      {/* Ações */}
                      <Grid
                        item
                        xs={12}
                        sm={1}
                        sx={{ textAlign: { xs: "left", sm: "right" } }}
                      >
                        <Tooltip title="Ver detalhes">
                          <IconButton
                            aria-label="Ver detalhes da autorização"
                            onClick={() => navigate(`/autorizacao/${it.id}`)}
                            sx={{
                              color: theme.palette.primary.main,
                              "&:hover": {
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.1
                                ),
                              },
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>

        <Box sx={{ overflowX: "auto", width: "100%" }}>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
          />
        </Box>
      </Box>
    </Container>
  );
}

export default MinhasAutorizacoes;
